import React from 'react';
import { notifications } from '../utils';
import { AzureBlobService } from './AzureBlobService';

export interface AttachmentUploadResult {
  fileName: string;
  blobUrl: string; // relative blob name stored in DB
  contentType: string;
  fileSizeBytes: number;
}

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

const ALLOWED_MIME_PREFIXES = ['image/', 'video/'];
const ALLOWED_MIME_EXACT = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation'
];

function isAllowedType(mimeType: string): boolean {
  if (ALLOWED_MIME_PREFIXES.some((prefix) => mimeType.startsWith(prefix))) return true;
  if (ALLOWED_MIME_EXACT.includes(mimeType)) return true;
  if (mimeType.startsWith('application/vnd.')) return true;
  return false;
}

const azureBlobService = new AzureBlobService();

export function useAttachmentUpload() {
  const [isUploading, setIsUploading] = React.useState(false);

  const uploadFile = async (file: File): Promise<AttachmentUploadResult | null> => {
    if (file.size > MAX_FILE_SIZE) {
      notifications.error('File exceeds 100 MB limit');
      return null;
    }

    if (!isAllowedType(file.type)) {
      notifications.error('File type not allowed');
      return null;
    }

    setIsUploading(true);
    try {
      const blobName = await azureBlobService.upload(file);

      return {
        fileName: file.name,
        blobUrl: blobName, // relative blob name; use AzureBlobService.getDownloadUrl() to open
        contentType: file.type,
        fileSizeBytes: file.size
      };
    } catch (e: any) {
      notifications.error(`Upload failed: ${e?.message ?? 'Unknown error'}`);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  return { uploadFile, isUploading };
}
